import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

// Redux Thunks & Selectors
import {
  fetchStudentPerformance,
  fetchChessOpeningsThunk,
  createTournamentPerformanceThunk,
  updateTournamentPerformanceThunk,
  deleteTournamentGameThunk,
} from "../../redux/admin/studentPerformance.thunk";
import {
  selectPerformanceData,
  selectPerformanceLoading,
  selectStudentAggregateStats,
  selectOpeningsList,
} from "../../redux/admin/studentPerformance.selector";
import { fetchStudents } from "../../redux/admin/student.thunk";
import { selectTournamentList } from "../../redux/admin/tournaments.selector";
import { fetchTournaments } from "../../redux/admin/tournaments.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "sp-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "sp-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

/* =====================================================
   SEARCHABLE OPENING COMBOBOX (FETCHED FROM API)
===================================================== */
const OpeningCombobox = ({ openings, value, onSelect, placeholder = "Search or select opening…" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return openings;
    return openings.filter((op) => 
      (op.name || "").toLowerCase().includes(q) || 
      (op.eco_code || "").toLowerCase().includes(q)
    );
  }, [openings, query]);

  useEffect(() => {
    if (!open && value) {
      setQuery(value);
    } else if (!value && !open) {
      setQuery("");
    }
  }, [value, open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const commit = (opName) => {
    onSelect(opName || "");
    setQuery(opName || "");
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="mnc-combobox" ref={wrapperRef}>
      <div className={`mnc-combobox-field ${open ? "is-open" : ""}`}>
        <svg className="mnc-combobox-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-3.8-3.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="sp-field mnc-combobox-input"
          placeholder={placeholder}
          value={query}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            onSelect(e.target.value);
            setOpen(true);
          }}
        />
        {value && !open && (
          <button
            type="button"
            className="mnc-combobox-clear"
            aria-label="Clear opening"
            onClick={() => commit("")}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <ul className="mnc-combobox-list" ref={listRef} role="listbox">
          {filtered.length === 0 && (
            <li className="mnc-combobox-empty">Custom opening: “{query}”</li>
          )}
          {filtered.map((op, index) => {
            const displayName = op.name || "Opening";
            const ecoCode = op.eco_code ? `(${op.eco_code})` : "";
            return (
              <li
                key={op.id || index}
                role="option"
                aria-selected={displayName === value}
                className={`mnc-combobox-option ${index === activeIndex ? "is-active" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(displayName);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <div className="mnc-opt-info">
                  <span className="mnc-combobox-option-name">{displayName}</span>
                  {ecoCode && <span className="mnc-combobox-option-adm">{ecoCode}</span>}
                </div>
                {displayName === value && <i className="fas fa-check text-primary" style={{ fontSize: "11px" }}></i>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* =====================================================
   SEARCHABLE STUDENT COMBOBOX (MNC STANDARD + RESPONSIVE)
===================================================== */
const StudentCombobox = ({ students, value, onSelect, disabled, placeholder = "Search student by name or admission number…" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(value)),
    [students, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) =>
        (s.name || s.full_name || "").toLowerCase().includes(q) ||
        String(s.admission_number || "").toLowerCase().includes(q)
    );
  }, [students, query]);

  useEffect(() => {
    if (!open && selectedStudent) {
      const name = selectedStudent.name || selectedStudent.full_name || "";
      setQuery(`${selectedStudent.admission_number || "ID:" + selectedStudent.id} — ${name}`);
    } else if (!value && !open) {
      setQuery("");
    }
  }, [selectedStudent, open, value]);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const commit = (student) => {
    onSelect(student ? String(student.id) : "");
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="mnc-combobox" ref={wrapperRef}>
      <div className={`mnc-combobox-field ${open ? "is-open" : ""}`}>
        <svg className="mnc-combobox-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-3.8-3.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="sp-field mnc-combobox-input fw-600"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => setQuery(e.target.value)}
        />
        {value && !open && (
          <button
            type="button"
            className="mnc-combobox-clear"
            aria-label="Clear selected student"
            onClick={() => commit(null)}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <ul className="mnc-combobox-list" ref={listRef} role="listbox">
          {filtered.length === 0 && (
            <li className="mnc-combobox-empty">No students match “{query}”</li>
          )}
          {filtered.map((student, index) => {
            const sName = student.name || student.full_name || "Student";
            const admNo = student.admission_number ? `#${student.admission_number}` : `ID: ${student.id}`;
            const gradeInfo = student.grade ? ` • ${student.grade}` : "";
            return (
              <li
                key={student.id}
                role="option"
                aria-selected={String(student.id) === String(value)}
                className={`mnc-combobox-option ${index === activeIndex ? "is-active" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(student);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <div className="mnc-opt-info">
                  <span className="mnc-combobox-option-name">{sName}</span>
                  <span className="mnc-combobox-option-adm">{admNo}{gradeInfo}</span>
                </div>
                {String(student.id) === String(value) && (
                  <i className="fas fa-check text-primary" style={{ fontSize: "12px" }}></i>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const StudentPerformance = () => {
  const dispatch = useDispatch();

  /* =====================================================
     LOCAL STATE (NO URL ROUTING / NO SEARCH PARAMS)
  ====================================================== */
  const [selectedStudentId, setSelectedStudentId] = useState("");

  /* =====================================================
     REDUX SELECTORS & STATES
  ====================================================== */
  const performanceData = useSelector(selectPerformanceData);
  const loading = useSelector(selectPerformanceLoading);
  const rawAggregateStats = useSelector(selectStudentAggregateStats);
  const openingsList = useSelector(selectOpeningsList);

  const studentsState = useSelector((state) => state.admin?.students || state.students || {});
  const globalStudents = useMemo(() => {
    return studentsState.studentList || studentsState.students || studentsState.data || [];
  }, [studentsState]);

  const tournamentList = useSelector(selectTournamentList) || [];
  const activeStudent = useMemo(() => {
    return globalStudents.find((s) => String(s.id) === String(selectedStudentId)) || null;
  }, [globalStudents, selectedStudentId]);

  /* =====================================================
     ACCURATE POINT & WINS CALCULATION HELPER
  ====================================================== */
  const calculateTournamentScore = (t) => {
    if (t.games && Array.isArray(t.games) && t.games.length > 0) {
      let pts = 0;
      t.games.forEach((g) => {
        const res = (g.result || "").toLowerCase();
        if (res === "win") pts += 1.0;
        else if (res === "draw") pts += 0.5;
      });
      return pts.toFixed(1);
    }
    const parsed = parseFloat(t.final_score);
    return !isNaN(parsed) ? parsed.toFixed(1) : "0.0";
  };

  const calculateTournamentWins = (t) => {
    if (t.games && Array.isArray(t.games) && t.games.length > 0) {
      let w = 0;
      t.games.forEach((g) => {
        if ((g.result || "").toLowerCase() === "win") w += 1;
      });
      return w;
    }
    return (t.white_wins || 0) + (t.black_wins || 0);
  };

  /* =====================================================
     AGGREGATE STATS RE-CALCULATION (3 KPIs: Tournaments, Points, Wins)
  ====================================================== */
  const aggregateStats = useMemo(() => {
    if (!performanceData || performanceData.length === 0) {
      return {
        tournamentCount: 0,
        totalScore: "0.0",
        totalWins: 0,
      };
    }

    let tCount = performanceData.length;
    let sScore = 0;
    let wCount = 0;

    performanceData.forEach((t) => {
      const tScore = parseFloat(calculateTournamentScore(t));
      if (!isNaN(tScore)) sScore += tScore;
      wCount += calculateTournamentWins(t);
    });

    const finalWins = wCount > 0 ? wCount : (rawAggregateStats?.totalWins || 0);
    const finalScore = sScore > 0 ? sScore.toFixed(1) : (rawAggregateStats?.totalScore || "0.0");

    return {
      tournamentCount: tCount,
      totalScore: finalScore,
      totalWins: finalWins,
    };
  }, [performanceData, rawAggregateStats]);

  /* =====================================================
     SEARCH & FILTER STATES
  ====================================================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedTournamentId, setExpandedTournamentId] = useState(null);

  /* =====================================================
     MODAL & TABS STATE
  ====================================================== */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("meta");
  const [saving, setSaving] = useState(false);
  const [isExternalTournament, setIsExternalTournament] = useState(false);

  /* =====================================================
     INITIAL FORM STATE (MASTER + CHILD)
  ====================================================== */
  const initialFormState = {
    id: null,
    student_id: selectedStudentId || "",
    tournament_id: "",
    tournament_name: "",
    category: "Open",
    tournament_date: new Date().toISOString().split("T")[0],
    final_score: "0.0",
    total_rounds: 5,
    final_rank: "",
    primary_failure_tag: [],
    is_repeated_mistake: 0,
    overall_coach_summary: "",
    games: [
      { round_number: 1, opponent_name: "", opponent_student_id: "", opponent_rating: 0, piece_color: "white", result: "win", opening_name: "", failure_tag: "NONE", coach_remarks: "", lichess_url: "" }
    ],
  };

  const [formData, setFormData] = useState(initialFormState);

  /* =====================================================
     AUTO-CALCULATE FINAL SCORE WHEN GAMES CHANGE IN FORM
  ====================================================== */
  useEffect(() => {
    if (formData.games && formData.games.length > 0) {
      let currentScore = 0;
      formData.games.forEach((g) => {
        const res = (g.result || "").toLowerCase();
        if (res === "win") currentScore += 1.0;
        else if (res === "draw") currentScore += 0.5;
      });
      setFormData((prev) => ({
        ...prev,
        final_score: currentScore.toFixed(1),
        total_rounds: Math.max(prev.total_rounds, formData.games.length),
      }));
    }
  }, [formData.games]);

  /* =====================================================
     FETCH EFFECTS
  ====================================================== */
  useEffect(() => {
    dispatch(fetchStudents({ page: 1, limit: 1000, search: "", status: "active" }));
    dispatch(fetchTournaments({ page: 1, limit: 100 }));
    dispatch(fetchChessOpeningsThunk(""));
  }, [dispatch]);

  useEffect(() => {
    if (selectedStudentId) {
      dispatch(fetchStudentPerformance({ studentId: selectedStudentId }));
    }
  }, [dispatch, selectedStudentId]);

  /* =====================================================
     HANDLERS & FORM HELPERS
  ====================================================== */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  const handlePrimaryFailureTagToggle = (tag) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.primary_failure_tag) ? prev.primary_failure_tag : [];
      if (current.includes(tag)) {
        return { ...prev, primary_failure_tag: current.filter((t) => t !== tag) };
      } else {
        return { ...prev, primary_failure_tag: [...current, tag] };
      }
    });
  };

  const handleTournamentSelectChange = (e) => {
    const val = e.target.value;
    if (val === "EXTERNAL") {
      setIsExternalTournament(true);
      setFormData((prev) => ({
        ...prev,
        tournament_id: "",
        tournament_name: "",
      }));
    } else {
      setIsExternalTournament(false);
      const selectedT = tournamentList.find((t) => String(t.id) === String(val));
      setFormData((prev) => ({
        ...prev,
        tournament_id: val,
        tournament_name: selectedT ? (selectedT.tournament_name || selectedT.name || "") : "",
        tournament_date: selectedT?.tournament_date || selectedT?.date || prev.tournament_date,
        category: selectedT?.category || prev.category,
      }));
    }
  };

  const handleGlobalStudentFilterSelect = (id) => {
    setSelectedStudentId(id);
    setExpandedTournamentId(null);
  };

  const handleStudentSelect = (id) => {
    setFormData((prev) => ({ ...prev, student_id: id }));
  };

  const addGameRound = () => {
    setFormData((prev) => {
      const nextRound = prev.games.length + 1;
      return {
        ...prev,
        games: [
          ...prev.games,
          { round_number: nextRound, opponent_name: "", opponent_student_id: "", opponent_rating: 0, piece_color: nextRound % 2 === 0 ? "black" : "white", result: "win", opening_name: "", failure_tag: "NONE", coach_remarks: "", lichess_url: "" }
        ],
      };
    });
  };

  const handleGameChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedGames = [...prev.games];
      updatedGames[index] = { ...updatedGames[index], [field]: value };
      return { ...prev, games: updatedGames };
    });
  };

  const handleOpponentStudentSelect = (index, studentId) => {
    setFormData((prev) => {
      const updatedGames = [...prev.games];
      if (studentId) {
        const matchedStudent = globalStudents.find((s) => String(s.id) === String(studentId));
        if (matchedStudent) {
          const sName = matchedStudent.name || matchedStudent.full_name || "";
          updatedGames[index] = {
            ...updatedGames[index],
            opponent_student_id: studentId,
            opponent_name: sName,
            opponent_rating: matchedStudent.chess_rating || matchedStudent.rating || updatedGames[index].opponent_rating,
          };
        }
      } else {
        updatedGames[index] = {
          ...updatedGames[index],
          opponent_student_id: "",
          opponent_name: "",
        };
      }
      return { ...prev, games: updatedGames };
    });
  };

  const removeGameRound = (index, gameId) => {
    if (formData.games.length === 1) {
      toastr.warning("At least one round game is required.");
      return;
    }

    if (!gameId) {
      setFormData((prev) => ({
        ...prev,
        games: prev.games.filter((_, i) => i !== index),
      }));
      return;
    }

    dispatch(deleteTournamentGameThunk({ gameId, tournamentId: formData.id }))
      .unwrap()
      .then(() => {
        toastr.success("Game round deleted.");
        setFormData((prev) => ({
          ...prev,
          games: prev.games.filter((_, i) => i !== index),
        }));
      })
      .catch((err) => toastr.error(err || "Failed to delete game."));
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setIsExternalTournament(false);
    setFormData({ ...initialFormState, student_id: selectedStudentId || "" });
    setActiveTab("meta");
    setShowModal(true);
  };

  const openEditModal = (tournament) => {
    setIsEditMode(true);
    const hasExistingTournamentId = Boolean(tournament.tournament_id);
    setIsExternalTournament(!hasExistingTournamentId && Boolean(tournament.tournament_name));
    
    const parsedPrimaryTags = Array.isArray(tournament.primary_failure_tag) 
      ? tournament.primary_failure_tag 
      : (typeof tournament.primary_failure_tag === "string" && tournament.primary_failure_tag !== "NONE" 
          ? tournament.primary_failure_tag.split(",").map(s => s.trim()) 
          : []);

    const parsedGames = tournament.games && tournament.games.length > 0 
      ? tournament.games.map(g => ({
          ...g,
          opponent_student_id: g.opponent_student_id || "",
          lichess_url: g.lichess_url || "",
          failure_tag: g.failure_tag || "NONE"
        })) 
      : initialFormState.games;

    setFormData({
      id: tournament.id,
      student_id: tournament.student_id,
      tournament_id: tournament.tournament_id || "",
      tournament_name: tournament.tournament_name || "",
      category: tournament.category || "Open",
      tournament_date: tournament.tournament_date || "",
      final_score: tournament.final_score || "0.0",
      total_rounds: tournament.total_rounds || 5,
      final_rank: tournament.final_rank || "",
      primary_failure_tag: parsedPrimaryTags,
      is_repeated_mistake: Number(tournament.is_repeated_mistake || 0),
      overall_coach_summary: tournament.overall_coach_summary || "",
      games: parsedGames,
    });
    setActiveTab("meta");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id) {
      toastr.warning("Please select a student profile.");
      setActiveTab("meta");
      return;
    }
    if (!formData.tournament_name && !formData.tournament_id) {
      toastr.warning("Tournament name or selection is required.");
      setActiveTab("meta");
      return;
    }

    const payload = {
      ...formData,
      primary_failure_tag: Array.isArray(formData.primary_failure_tag) && formData.primary_failure_tag.length > 0
        ? formData.primary_failure_tag.join(",")
        : "NONE",
    };

    setSaving(true);
    try {
      if (isEditMode && formData.id) {
        await dispatch(updateTournamentPerformanceThunk({ id: formData.id, payload })).unwrap();
        toastr.success("Tournament performance updated.");
      } else {
        await dispatch(createTournamentPerformanceThunk(payload)).unwrap();
        toastr.success("Tournament record added.");
      }

      setShowModal(false);

      if (String(formData.student_id) !== String(selectedStudentId)) {
        setSelectedStudentId(formData.student_id);
      } else {
        dispatch(fetchStudentPerformance({ studentId: selectedStudentId }));
      }
    } catch (err) {
      toastr.error(err || "Failed to save tournament record.");
    } finally {
      setSaving(false);
    }
  };

  const filteredTournaments = performanceData.filter((t) =>
    (t.tournament_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const studentName = activeStudent ? (activeStudent.full_name || activeStudent.name || activeStudent.student_name || "Student") : `Student #${selectedStudentId || "N/A"}`;
  const studentInitial = (studentName[0] || "S").toUpperCase();

  const FAILURE_TAG_OPTIONS = [
    "NONE",
    "OPENING_MISPLAY",
    "TACTICAL_BLUNDER",
    "CALCULATION_OVERSIGHT",
    "POSITIONAL_MISTAKE",
    "STRATEGIC_FLAW",
    "ENDGAME_ERROR",
    "CONVERSION_FAILURE",
    "TIME_TROUBLE",
    "PSYCHOLOGICAL_LAPSE"
  ];

  return (
    <div className="mnc-app-container">
      <div className="mnc-main-content">

        {/* ── TOP MNC HEADER ── */}
        <div className="mnc-header-card">
          <div className="mnc-header-left">
            <div className="mnc-app-badge">Aadhav Academy • Enterprise Suite</div>
            <h1 className="mnc-page-title">Tournament Progress & Analysis</h1>
            <p className="mnc-page-subtitle">Inspect comprehensive match breakdowns, tactical error tags, and coach evaluations.</p>
          </div>
          {selectedStudentId && (
            <button type="button" className="mnc-btn-primary" onClick={openAddModal}>
              <i className="fas fa-plus"></i> Link Tournament Record
            </button>
          )}
        </div>

        {/* ── STUDENT SELECTOR BAR ── */}
        <div className="mnc-filter-section">
          <div className="mnc-filter-label-wrap">
            <span className="mnc-filter-icon"><i className="fas fa-user-graduate"></i></span>
            <label className="mnc-label mb-0">Target Student Profile Selection</label>
          </div>
          <StudentCombobox
            students={globalStudents}
            value={selectedStudentId}
            onSelect={handleGlobalStudentFilterSelect}
            placeholder="Search and select student by name or admission number..."
          />
        </div>

        {!selectedStudentId ? (
          <div className="mnc-empty-container">
            <div className="mnc-empty-icon-wrap"><i className="fas fa-chess-knight"></i></div>
            <h3 className="mnc-empty-title">No Student Selected</h3>
            <p className="mnc-empty-desc">Please search and choose a student profile from the combobox above to inspect tournament performance and match records.</p>
          </div>
        ) : (
          <>
            {/* ── STUDENT EXECUTIVE SUMMARY BANNER ── */}
            <div className="mnc-profile-banner">
              <div className="mnc-avatar-box">{studentInitial}</div>
              <div className="mnc-profile-details">
                <h2>{studentName}</h2>
                <p>
                  <span><strong>Admission No:</strong> {activeStudent?.admission_number || "—"}</span>
                  <span className="mx-2">•</span>
                  <span><strong>Grade/Standard:</strong> {activeStudent?.grade || "Standard"}</span>
                  <span className="mx-2">•</span>
                  <span><strong>Mobile:</strong> {activeStudent?.mobile_no || "—"}</span>
                </p>
              </div>
            </div>

            {/* ── MNC EXECUTIVE KPI STRIP (3 Core KPIs) ── */}
            <div className="mnc-kpi-grid-3">
              {[
                { label: "Tournaments Played", value: aggregateStats.tournamentCount, icon: "fa-trophy" },
                { label: "Total Points Scored", value: `${aggregateStats.totalScore} pts`, icon: "fa-star" },
                { label: "Total Wins", value: aggregateStats.totalWins, icon: "fa-check-circle" },
              ].map(({ label, value, icon }) => (
                <div key={label} className="mnc-kpi-card">
                  <div className="mnc-kpi-top">
                    <span className="mnc-kpi-label">{label}</span>
                    <i className={`fas ${icon} mnc-kpi-ico`}></i>
                  </div>
                  <div className="mnc-kpi-val">{loading ? <Skeleton w={60} h={24} r={6} /> : value}</div>
                </div>
              ))}
            </div>

            {/* ── TOOLBAR ── */}
            <div className="mnc-toolbar">
              <div className="mnc-search-wrapper">
                <i className="fas fa-search mnc-search-ico"></i>
                <input
                  type="text"
                  className="mnc-search-input"
                  placeholder="Filter tournaments by name…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* ── ACCORDION TOURNAMENT LIST & GAMES ── */}
            <div className="mnc-stack">
              {loading ? (
                Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="mnc-card p-4 mb-3">
                    <Skeleton w="35%" h={18} className="mb-3" />
                    <Skeleton w="80%" h={14} />
                  </div>
                ))
              ) : filteredTournaments.length === 0 ? (
                <div className="mnc-empty-container">
                  <div className="mnc-empty-icon-wrap"><i className="fas fa-chess-board"></i></div>
                  <h3 className="mnc-empty-title">No Tournament Records Found</h3>
                  <p className="mnc-empty-desc mb-3">There are no tournament logs saved for this student profile yet.</p>
                  <button type="button" className="mnc-btn-primary mx-auto" onClick={openAddModal}>
                    Link Tournament Record
                  </button>
                </div>
              ) : (
                filteredTournaments.map((t) => {
                  const isExpanded = expandedTournamentId === t.id;
                  const games = t.games || [];
                  const computedScore = calculateTournamentScore(t);
                  
                  const primaryTagsList = typeof t.primary_failure_tag === "string"
                    ? t.primary_failure_tag.split(",").map(s => s.trim()).filter(Boolean)
                    : (Array.isArray(t.primary_failure_tag) ? t.primary_failure_tag : []);

                  return (
                    <div key={t.id} className={`mnc-tournament-card ${isExpanded ? "is-expanded" : ""}`}>
                      
                      {/* Tournament Summary Header */}
                      <div className="mnc-t-header" onClick={() => setExpandedTournamentId(isExpanded ? null : t.id)}>
                        <div className="mnc-t-left">
                          <div className="mnc-t-icon"><i className="fas fa-award"></i></div>
                          <div>
                            <h3 className="mnc-t-name">{t.tournament_name}</h3>
                            <div className="mnc-t-meta-row">
                              <span><i className="far fa-calendar-alt me-1"></i> {t.tournament_date || "—"}</span>
                              <span className="mx-1">•</span>
                              <span><i className="fas fa-layer-group me-1"></i> {t.category || "Open"}</span>
                              <span className="mx-1">•</span>
                              <span><i className="fas fa-chess me-1"></i> {games.length} Rounds</span>
                            </div>
                            {primaryTagsList.length > 0 && primaryTagsList[0] !== "NONE" && (
                              <div className="d-flex flex-wrap gap-1 mt-2 align-items-center">
                                <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}>Primary Tags:</span>
                                {primaryTagsList.map(tag => (
                                  <span key={tag} className="mnc-pill pill-coral" style={{ fontSize: "10px" }}>{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mnc-t-right">
                          <div className="mnc-score-pill">
                            <span className="lbl">Score</span>
                            <span className="val">{computedScore} / {t.total_rounds || 7}</span>
                          </div>
                          {t.final_rank && (
                            <div className="mnc-rank-pill">
                              Rank <strong>#{t.final_rank}</strong>
                            </div>
                          )}
                          <div className="mnc-t-btns">
                            <button
                              type="button"
                              className="mnc-btn-outline-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(t);
                              }}
                            >
                              <i className="fas fa-edit me-1"></i> Edit
                            </button>
                            <span className="mnc-chevron">
                              <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Expandable Round-by-Round Game Log Section */}
                      {isExpanded && (
                        <div className="mnc-t-body">
                          <div className="mnc-games-header-row">
                            <h4>Round-by-Round Breakdown & Coach Analysis</h4>
                            {t.overall_coach_summary && (
                              <div className="mnc-coach-note-box">
                                <strong>Overall Coach Evaluation:</strong> {t.overall_coach_summary}
                              </div>
                            )}
                          </div>

                          {games.length === 0 ? (
                            <p className="text-muted small py-3 text-center">No round games recorded for this tournament.</p>
                          ) : (
                            <div className="mnc-rounds-grid">
                              {games.map((g) => {
                                const isWin = (g.result || "").toLowerCase() === "win";
                                const isDraw = (g.result || "").toLowerCase() === "draw";
                                const resultClass = isWin ? "res-win" : isDraw ? "res-draw" : "res-loss";
                                const colorIcon = (g.piece_color || "").toLowerCase() === "white" ? "fa-chess-king text-secondary" : "fa-chess-king text-dark";

                                const matchedOpponent = globalStudents.find(s => String(s.id) === String(g.opponent_student_id));
                                const gameTag = g.failure_tag || "NONE";

                                return (
                                  <div key={g.id} className="mnc-round-card">
                                    <div className="mnc-r-head">
                                      <span className="mnc-r-badge">Round {g.round_number}</span>
                                      <span className={`mnc-r-result ${resultClass}`}>{g.result.toUpperCase()}</span>
                                    </div>
                                    <div className="mnc-r-body">
                                      <div className="mnc-r-row">
                                        <span className="lbl"><i className={`fas ${colorIcon} me-1`}></i> Color:</span>
                                        <span className="val text-capitalize">{g.piece_color || "—"}</span>
                                      </div>
                                      <div className="mnc-r-row">
                                        <span className="lbl">Opponent:</span>
                                        <span className="val fw-600">
                                          {g.opponent_name || "—"} {g.opponent_rating > 0 ? `(${g.opponent_rating})` : ""}
                                          {matchedOpponent && (
                                            <span className="ms-1 badge bg-light text-primary border" style={{ fontSize: "9px" }} title="Academy Student Profile Linked">
                                              <i className="fas fa-link me-1"></i> Academy Student
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                      <div className="mnc-r-row">
                                        <span className="lbl">Opening:</span>
                                        <span className="val">{g.opening_name || "—"}</span>
                                      </div>
                                      <div className="mnc-r-row">
                                        <span className="lbl">Failure Tag:</span>
                                        <span className={`mnc-pill ${gameTag === "NONE" ? "pill-green" : "pill-coral"}`}>
                                          {gameTag}
                                        </span>
                                      </div>
                                      {g.lichess_url && (
                                        <div className="mnc-r-row">
                                          <span className="lbl">Lichess Game:</span>
                                          <a href={g.lichess_url} target="_blank" rel="noopener noreferrer" className="val text-primary fw-600 text-decoration-none d-flex align-items-center gap-1">
                                            <i className="fas fa-external-link-alt" style={{ fontSize: "10px" }}></i> View Analysis
                                          </a>
                                        </div>
                                      )}
                                      {g.coach_remarks && (
                                        <div className="mnc-r-remarks mt-2">
                                          <em>"{g.coach_remarks}"</em>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════
          MNC EXECUTIVE MODAL
      ═══════════════════════════════════════ */}
      {showModal && (
        <div className="mnc-modal-overlay" onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}>
          <div className="mnc-modal-container">

            {/* Sidebar Navigation */}
            <div className="mnc-modal-sidebar">
              <div className="mnc-modal-side-top">
                <p className="mnc-modal-side-title">Tournament Log</p>
                <p className="mnc-modal-side-sub">Enterprise coaching workspace</p>
              </div>
              <nav className="mnc-modal-nav">
                {[
                  { key: "meta", icon: "fas fa-trophy", label: "Tournament Meta" },
                  { key: "games", icon: "fas fa-chess-knight", label: `Round Games (${formData.games.length})` },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`mnc-nav-btn${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                    {key === "games" && formData.games.length > 0 && <span className="mnc-nav-counter">{formData.games.length}</span>}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main Form Panel */}
            <form onSubmit={handleSubmit} className="mnc-modal-form-wrap">
              <div className="mnc-modal-header">
                <div>
                  <h2 className="mnc-modal-title">{isEditMode ? "Edit tournament record" : "Link new tournament"}</h2>
                  <p className="mnc-modal-sub">Record student profile, tournament scores, and round match analysis.</p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} aria-label="Close" disabled={saving}></button>
              </div>

              <div className="mnc-modal-body">
                <div className="row g-3">

                  {/* ── TAB 1: TOURNAMENT META ── */}
                  {activeTab === "meta" && (
                    <>
                      <div className="col-12">
                        <label className="sp-label">Student Profile *</label>
                        <StudentCombobox
                          students={globalStudents}
                          value={formData.student_id}
                          onSelect={handleStudentSelect}
                          disabled={isEditMode}
                        />
                      </div>
                      <div className="col-12">
                        <label className="sp-label">Select Academy Tournament *</label>
                        <select
                          name="tournament_id"
                          className="sp-field"
                          value={isExternalTournament ? "EXTERNAL" : (formData.tournament_id || "")}
                          onChange={handleTournamentSelectChange}
                        >
                          <option value="">-- Choose registered tournament or enter external --</option>
                          {tournamentList.map((t) => {
                            const tName = t.tournament_name || t.name;
                            const tDate = t.tournament_date || t.date;
                            return (
                              <option key={t.id} value={t.id}>
                                {tDate ? `${tName} (${tDate})` : tName}
                              </option>
                            );
                          })}
                          <option value="EXTERNAL">+ Enter External / Custom Tournament</option>
                        </select>
                      </div>

                      {(isExternalTournament || !formData.tournament_id) && (
                        <div className="col-12">
                          <label className="sp-label">Custom / External Tournament Name *</label>
                          <input
                            type="text"
                            name="tournament_name"
                            className="sp-field"
                            placeholder="e.g. 2nd District Level Open Chess"
                            value={formData.tournament_name}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      )}

                      <div className="col-md-6">
                        <label className="sp-label">Category</label>
                        <input type="text" name="category" className="sp-field" placeholder="e.g. Under-12 Open" value={formData.category} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sp-label">Tournament Date</label>
                        <input type="date" name="tournament_date" className="sp-field" value={formData.tournament_date} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="sp-label">Final Score</label>
                        <input type="number" step="0.5" name="final_score" className="sp-field fw-600 text-primary bg-light" value={formData.final_score} readOnly />
                      </div>
                      <div className="col-md-4">
                        <label className="sp-label">Total Rounds</label>
                        <input type="number" name="total_rounds" className="sp-field" placeholder="7" value={formData.total_rounds} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="sp-label">Final Rank</label>
                        <input type="number" name="final_rank" className="sp-field" placeholder="12" value={formData.final_rank} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="sp-label">Primary Failure Tags (Multi-select)</label>
                        <div className="mnc-tag-checkbox-grid">
                          {FAILURE_TAG_OPTIONS.filter(tag => tag !== "NONE").map((tag) => {
                            const isChecked = Array.isArray(formData.primary_failure_tag) && formData.primary_failure_tag.includes(tag);
                            return (
                              <label key={tag} className={`mnc-tag-checkbox-pill ${isChecked ? "is-checked" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handlePrimaryFailureTagToggle(tag)}
                                  style={{ display: "none" }}
                                />
                                <i className={`fas ${isChecked ? "fa-check-square text-primary" : "fa-square text-muted"} me-1`}></i>
                                {tag}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div className="col-md-12 d-flex align-items-center">
                        <div className="mnc-toggle-box w-100">
                          <div>
                            <label className="sp-label mb-0">Repeated Mistake?</label>
                            <span className="mnc-toggle-sub">Flag if recurring issue.</span>
                          </div>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input sp-switch" type="checkbox" name="is_repeated_mistake" checked={Number(formData.is_repeated_mistake) === 1} onChange={handleChange} />
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="sp-label">Overall Coach Summary & Remarks</label>
                        <textarea name="overall_coach_summary" className="sp-field" rows="3" placeholder="Write comprehensive coaching observations…" value={formData.overall_coach_summary} onChange={handleChange}></textarea>
                      </div>
                    </>
                  )}

                  {/* ── TAB 2: ROUND GAMES ── */}
                  {activeTab === "games" && (
                    <>
                      <div className="col-12">
                        <div className="sp-section-note">
                          Log round-by-round match outcomes, piece color, searchable opening from database, single failure tag per round, and Lichess link. Final score is automatically computed.
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="mnc-games-scroll">
                          {formData.games.map((game, index) => {
                            return (
                              <div key={game.id || index} className="mnc-game-item">
                                <div className="mnc-game-item-header">
                                  <span className="mnc-game-badge">Round {game.round_number || index + 1}</span>
                                  <button type="button" className="mnc-game-remove-btn" onClick={() => removeGameRound(index, game.id)} title="Remove round">
                                    <i className="fas fa-times"></i> Remove
                                  </button>
                                </div>
                                <div className="row g-2 align-items-end">
                                  <div className="col-12 mb-1">
                                    <label className="sp-label text-primary">Link Academy Student Profile (Optional Opponent)</label>
                                    <StudentCombobox
                                      students={globalStudents.filter((s) => String(s.id) !== String(formData.student_id))}
                                      value={game.opponent_student_id || ""}
                                      onSelect={(id) => handleOpponentStudentSelect(index, id)}
                                      placeholder="Search opponent student by name or admission number…"
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label className="sp-label">Opponent Name</label>
                                    <input
                                      type="text"
                                      className="sp-field"
                                      placeholder="Opponent Full Name"
                                      value={game.opponent_name || ""}
                                      onChange={(e) => handleGameChange(index, "opponent_name", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <label className="sp-label">Color</label>
                                    <select className="sp-field" value={game.piece_color || "white"} onChange={(e) => handleGameChange(index, "piece_color", e.target.value)}>
                                      <option value="white">White</option>
                                      <option value="black">Black</option>
                                    </select>
                                  </div>
                                  <div className="col-md-3">
                                    <label className="sp-label">Result</label>
                                    <select className="sp-field" value={game.result || "win"} onChange={(e) => handleGameChange(index, "result", e.target.value)}>
                                      <option value="win">Win (+1.0)</option>
                                      <option value="draw">Draw (+0.5)</option>
                                      <option value="loss">Loss (0.0)</option>
                                    </select>
                                  </div>
                                  <div className="col-md-6">
                                    <label className="sp-label">Opening Line</label>
                                    <OpeningCombobox
                                      openings={openingsList}
                                      value={game.opening_name || ""}
                                      onSelect={(op) => handleGameChange(index, "opening_name", op)}
                                      placeholder="Search or select database opening..."
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label className="sp-label">Failure Tag (Single Select)</label>
                                    <select
                                      className="sp-field"
                                      value={game.failure_tag || "NONE"}
                                      onChange={(e) => handleGameChange(index, "failure_tag", e.target.value)}
                                    >
                                      {FAILURE_TAG_OPTIONS.map((tag) => (
                                        <option key={tag} value={tag}>{tag}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="col-12">
                                    <label className="sp-label">Lichess Analysis URL</label>
                                    <input
                                      type="url"
                                      className="sp-field"
                                      placeholder="https://lichess.org/..."
                                      value={game.lichess_url || ""}
                                      onChange={(e) => handleGameChange(index, "lichess_url", e.target.value)}
                                    />
                                  </div>
                                  <div className="col-12">
                                    <label className="sp-label">Coach Remarks</label>
                                    <input type="text" className="sp-field" placeholder="Specific game notes…" value={game.coach_remarks || ""} onChange={(e) => handleGameChange(index, "coach_remarks", e.target.value)} />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <button type="button" className="mnc-add-round-btn" onClick={addGameRound}>
                          <i className="fas fa-plus"></i> Add Round Game
                        </button>
                      </div>
                    </>
                  )}

                </div>
              </div>

              {/* ── MODAL FOOTER ── */}
              <div className="mnc-modal-footer">
                <button type="button" className="mnc-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="mnc-btn-primary mnc-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      Saving changes…
                    </>
                  ) : (
                    isEditMode ? "Save changes" : "Create record"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MNC CORPORATE STYLING & MOBILE RESPONSIVE ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;0,14..32,700;1,14..32,400&display=swap');

        @keyframes sp-spin { to { transform: rotate(360deg); } }
        @keyframes sp-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes mnc-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes mnc-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        .mnc-app-container {
          background: #f4f5f7; min-height: 100vh; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #111827; font-size: 13.5px; line-height: 1.5; padding: 2rem 1.5rem;
        }
        .mnc-main-content { max-width: 1240px; margin: 0 auto; }

        /* MNC Header Card */
        .mnc-header-card {
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 24px 28px;
          display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap;
          box-shadow: 0 1px 3px rgba(0,0,0,0.02); margin-bottom: 1.25rem;
        }
        .mnc-app-badge {
          display: inline-block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em;
          color: #2563eb; background: #eff6ff; padding: 3px 10px; border-radius: 20px; margin-bottom: 8px;
        }
        .mnc-page-title { font-size: 1.4rem; font-weight: 700; color: #111827; letter-spacing: -0.025em; margin: 0 0 4px; }
        .mnc-page-subtitle { font-size: 13px; color: #6b7280; margin: 0; }

        /* Filter Section */
        .mnc-filter-section {
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 18px 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.02); margin-bottom: 1.25rem;
        }
        .mnc-filter-label-wrap { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
        .mnc-filter-icon { color: #2563eb; font-size: 14px; }

        /* Student Profile Banner */
        .mnc-profile-banner {
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 18px 24px;
          display: flex; align-items: center; gap: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.02); margin-bottom: 1.25rem;
        }
        .mnc-avatar-box {
          width: 48px; height: 48px; border-radius: 10px; background: #2563eb; color: #ffffff;
          font-size: 20px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
          box-shadow: 0 2px 4px rgba(37,99,235,0.2);
        }
        .mnc-profile-details h2 { font-size: 1.05rem; font-weight: 700; color: #111827; margin: 0 0 4px; }
        .mnc-profile-details p { font-size: 12.5px; color: #4b5563; margin: 0; }

        /* 3-Column KPI Grid */
        .mnc-kpi-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 1.25rem; }
        @media (max-width: 768px) { .mnc-kpi-grid-3 { grid-template-columns: 1fr; } }
        .mnc-kpi-card {
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px 20px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.02); display: flex; flex-direction: column; gap: 6px;
        }
        .mnc-kpi-top { display: flex; justify-content: space-between; align-items: center; }
        .mnc-kpi-label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
        .mnc-kpi-ico { color: #2563eb; font-size: 13px; opacity: 0.8; }
        .mnc-kpi-val { font-size: 1.4rem; font-weight: 700; color: #111827; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        /* Toolbar */
        .mnc-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; }
        .mnc-search-wrapper { flex: 1; max-width: 360px; position: relative; }
        .mnc-search-ico { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9ca3af; font-size: 13px; }
        .mnc-search-input {
          width: 100%; background: #ffffff; border: 1px solid #d1d5db; border-radius: 10px;
          padding: 9px 14px 9px 38px; font-size: 13px; color: #111827; outline: none; font-family: inherit;
          box-shadow: 0 1px 2px rgba(0,0,0,0.02);
        }
        .mnc-search-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }

        /* Primary Button */
        .mnc-btn-primary {
          background: #2563eb; color: #ffffff; border: none; border-radius: 10px; padding: 10px 20px;
          font-size: 13.5px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
          transition: background 0.15s, box-shadow 0.15s; font-family: inherit; box-shadow: 0 1px 2px rgba(37,99,235,0.2);
        }
        .mnc-btn-primary:hover:not(:disabled) { background: #1d4ed8; }
        .mnc-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .mnc-btn-submit { padding: 9px 22px; min-width: 158px; justify-content: center; }

        /* Tournament Stack Accordion */
        .mnc-stack { display: flex; flex-direction: column; gap: 12px; }
        .mnc-tournament-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.02); transition: box-shadow 0.15s; }
        .mnc-tournament-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
        .mnc-t-header { padding: 16px 22px; display: flex; align-items: center; justify-content: space-between; cursor: pointer; background: #ffffff; gap: 12px; flex-wrap: wrap; }
        .mnc-t-left { display: flex; align-items: center; gap: 14px; }
        .mnc-t-icon { width: 40px; height: 40px; border-radius: 10px; background: #eff6ff; color: #2563eb; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
        .mnc-t-name { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 3px; }
        .mnc-t-meta-row { display: flex; align-items: center; font-size: 12px; color: #6b7280; flex-wrap: wrap; }
        .mnc-t-right { display: flex; align-items: center; gap: 16px; }
        .mnc-score-pill { background: #f9fafb; border: 1px solid #e5e7eb; padding: 5px 12px; border-radius: 8px; text-align: center; }
        .mnc-score-pill .lbl { display: block; font-size: 9.5px; text-transform: uppercase; color: #9ca3af; font-weight: 700; }
        .mnc-score-pill .val { font-size: 13.5px; font-weight: 700; color: #2563eb; }
        .mnc-rank-pill { font-size: 12px; color: #92400e; background: #fef3c7; border: 1px solid #fde68a; padding: 5px 10px; border-radius: 8px; font-weight: 500; }
        .mnc-t-btns { display: flex; align-items: center; gap: 10px; }
        .mnc-chevron { color: #9ca3af; font-size: 13px; }

        .mnc-t-body { background: #f9fafb; border-top: 1px solid #e5e7eb; padding: 20px 24px; }
        .mnc-games-header-row h4 { font-size: 12.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #4b5563; margin: 0 0 10px; }
        .mnc-coach-note-box { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 14px; font-size: 12.5px; color: #374151; margin-bottom: 16px; }

        .mnc-rounds-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
        .mnc-round-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; display: flex; flex-direction: column; gap: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
        .mnc-r-head { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f3f4f6; padding-bottom: 6px; }
        .mnc-r-badge { font-size: 11px; font-weight: 700; color: #2563eb; background: #eff6ff; padding: 2px 8px; border-radius: 6px; }
        .mnc-r-result { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 6px; letter-spacing: 0.03em; }
        .res-win { background: #dcfce7; color: #166534; }
        .res-draw { background: #fef3c7; color: #92400e; }
        .res-loss { background: #fee2e2; color: #991b1b; }
        .mnc-r-body { display: flex; flex-direction: column; gap: 5px; font-size: 12.5px; }
        .mnc-r-row { display: flex; justify-content: space-between; align-items: center; }
        .mnc-r-row .lbl { color: #6b7280; font-weight: 500; font-size: 11.5px; }
        .mnc-r-remarks { background: #f9fafb; border-radius: 6px; padding: 6px 10px; font-size: 11.5px; color: #4b5563; font-style: italic; border: 1px solid #f3f4f6; }

        .mnc-pill { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 10.5px; font-weight: 600; }
        .pill-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .pill-coral { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

        .mnc-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; }
        .mnc-btn-outline-sm {
          border: 1px solid #d1d5db; background: #ffffff; border-radius: 8px; padding: 6px 12px;
          font-size: 12px; font-weight: 600; color: #374151; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .mnc-btn-outline-sm:hover { background: #f9fafb; color: #111827; }

        /* Empty states */
        .mnc-empty-container { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; text-align: center; padding: 3.5rem 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
        .mnc-empty-icon-wrap {
          width: 52px; height: 52px; background: #f3f4f6; border-radius: 14px;
          display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #9ca3af; margin-bottom: 12px;
        }
        .mnc-empty-title { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 4px; }
        .mnc-empty-desc { font-size: 13px; color: #6b7280; max-width: 440px; margin: 0 auto; }

        /* MNC Combobox styles */
        .mnc-combobox { position: relative; width: 100%; }
        .mnc-combobox-field { position: relative; display: flex; align-items: center; }
        .mnc-combobox-icon { position: absolute; left: 14px; color: #9ca3af; pointer-events: none; }
        .mnc-combobox-input { padding-left: 38px !important; padding-right: 32px !important; }
        .mnc-combobox-clear { position: absolute; right: 10px; width: 22px; height: 22px; border: none; background: #f3f4f6; color: #4b5563; border-radius: 50%; font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.12s; }
        .mnc-combobox-clear:hover { background: #e5e7eb; color: #111827; }
        .mnc-combobox-list {
          position: absolute; z-index: 50; top: calc(100% + 6px); left: 0; right: 0; max-height: 260px; overflow-y: auto;
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px; box-shadow: 0 12px 30px -8px rgba(0,0,0,0.12);
          margin: 0; padding: 6px; list-style: none;
        }
        .mnc-combobox-option {
          display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; border-radius: 8px; cursor: pointer;
          font-size: 13px; color: #374151; transition: background 0.1s, color 0.1s;
        }
        .mnc-opt-info { display: flex; flex-direction: column; gap: 2px; }
        .mnc-combobox-option-name { font-weight: 600; color: #111827; }
        .mnc-combobox-option-adm { font-size: 11.5px; color: #6b7280; }
        .mnc-combobox-option.is-active, .mnc-combobox-option:hover { background: #eff6ff; color: #2563eb; }
        .mnc-combobox-option.is-active .mnc-combobox-option-name, .mnc-combobox-option:hover .mnc-combobox-option-name { color: #2563eb; }
        .mnc-combobox-option.is-active .mnc-combobox-option-adm, .mnc-combobox-option:hover .mnc-combobox-option-adm { color: #3b82f6; }
        .mnc-combobox-empty { padding: 14px; text-align: center; color: #9ca3af; font-size: 12.5px; }

        /* Checkbox pills grid */
        .mnc-tag-checkbox-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
        .mnc-tag-checkbox-pill {
          display: inline-flex; align-items: center; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 8px;
          padding: 6px 12px; font-size: 12px; font-weight: 500; color: #374151; cursor: pointer; transition: all 0.15s; user-select: none;
        }
        .mnc-tag-checkbox-pill:hover { background: #e5e7eb; }
        .mnc-tag-checkbox-pill.is-checked { background: #eff6ff; border-color: #2563eb; color: #2563eb; font-weight: 600; }

        /* Modal Layout with full mobile responsiveness */
        .mnc-modal-overlay {
          position: fixed; inset: 0; background: rgba(17,24,39,0.5); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: mnc-overlay-in 0.2s ease;
        }
        .mnc-modal-container {
          width: 100%; max-width: 920px; height: 620px; background: #ffffff; border-radius: 16px; border: 1px solid #e5e7eb;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); display: flex; overflow: hidden; animation: mnc-modal-in 0.22s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .mnc-modal-sidebar { width: 230px; background: #f9fafb; border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; flex-shrink: 0; }
        .mnc-modal-side-top { padding: 20px 18px; border-bottom: 1px solid #e5e7eb; }
        .mnc-modal-side-title { font-size: 13.5px; font-weight: 700; color: #111827; margin: 0; }
        .mnc-modal-side-sub { font-size: 11px; color: #6b7280; margin: 3px 0 0; }
        .mnc-modal-nav { padding: 10px; display: flex; flex-direction: column; gap: 3px; }
        .mnc-nav-btn {
          display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px; border: none; background: transparent;
          font-size: 13px; color: #4b5563; font-weight: 500; cursor: pointer; text-align: left; width: 100%; transition: all 0.12s; font-family: inherit;
        }
        .mnc-nav-btn i { font-size: 13px; width: 16px; text-align: center; }
        .mnc-nav-btn span { flex: 1; }
        .mnc-nav-btn.active { background: #ffffff; color: #2563eb; font-weight: 600; border: 1px solid #e5e7eb; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
        .mnc-nav-counter { background: #2563eb; color: #ffffff; font-size: 10px; font-weight: 700; border-radius: 20px; padding: 1px 6px; }

        .mnc-modal-form-wrap { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .mnc-modal-header { padding: 18px 28px; border-bottom: 1px solid #f3f4f6; display: flex; align-items: flex-start; justify-content: space-between; }
        .mnc-modal-title { font-size: 16px; font-weight: 700; color: #111827; margin: 0; }
        .mnc-modal-sub { font-size: 12px; color: #6b7280; margin: 3px 0 0; }
        .mnc-modal-body { flex: 1; overflow-y: auto; padding: 22px 28px; }
        .mnc-modal-footer { padding: 14px 28px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f9fafb; }

        /* Form elements */
        .sp-label { display: block; font-size: 11px; font-weight: 700; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .sp-field {
          width: 100%; border: 1px solid #d1d5db; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #111827;
          background: #ffffff; outline: none; font-family: inherit; transition: border-color 0.15s, box-shadow 0.15s;
        }
        .sp-field:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }
        select.sp-field { cursor: pointer; }

        .sp-section-note { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 9px; padding: 10px 14px; font-size: 12.5px; color: #4b5563; margin-bottom: 12px; }

        .mnc-toggle-box {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 9px; padding: 11px 16px; height: 100%;
        }
        .mnc-toggle-sub { font-size: 11px; color: #9ca3af; }

        .mnc-games-scroll { display: flex; flex-direction: column; gap: 12px; max-height: 310px; overflow-y: auto; padding-right: 4px; margin-bottom: 10px; }
        .mnc-game-item { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; }
        .mnc-game-item-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .mnc-game-remove-btn { background: transparent; border: none; color: #dc2626; font-size: 11.5px; font-weight: 600; cursor: pointer; }
        .mnc-game-remove-btn:hover { text-decoration: underline; }

        .mnc-add-round-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 10px;
          border: 1.5px dashed #d1d5db; background: transparent; border-radius: 9px; font-size: 13px; font-weight: 600;
          color: #4b5563; cursor: pointer; transition: all 0.15s; font-family: inherit;
        }
        .mnc-add-round-btn:hover { border-color: #2563eb; color: #2563eb; background: #eff6ff; }

        .mnc-btn-cancel {
          border: 1px solid #d1d5db; background: #ffffff; border-radius: 9px; padding: 9px 18px;
          font-size: 13px; font-weight: 600; color: #4b5563; cursor: pointer; font-family: inherit;
        }
        .mnc-btn-cancel:hover { background: #f9fafb; color: #111827; }

        /* Media queries for complete mobile responsiveness */
        @media (max-width: 768px) {
          .mnc-app-container { padding: 1rem 0.75rem; }
          .mnc-header-card { padding: 16px 18px; }
          .mnc-page-title { font-size: 1.2rem; }
          .mnc-kpi-grid-3 { grid-template-columns: 1fr; gap: 8px; }
          .mnc-kpi-card { padding: 12px 14px; }
          .mnc-kpi-val { font-size: 1.2rem; }
          .mnc-t-header { padding: 14px 16px; }
          .mnc-t-right { width: 100%; justify-content: space-between; margin-top: 8px; border-top: 1px solid #f3f4f6; padding-top: 8px; }
          .mnc-t-body { padding: 14px 16px; }
          .mnc-rounds-grid { grid-template-columns: 1fr; }
          
          .mnc-modal-overlay { padding: 8px; }
          .mnc-modal-container { flex-direction: column; height: 92vh; max-height: none; }
          .mnc-modal-sidebar { width: 100%; border-right: none; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; }
          .mnc-modal-side-top { padding: 12px 16px; }
          .mnc-modal-nav { flex-direction: row; overflow-x: auto; padding: 6px 12px; gap: 6px; }
          .mnc-nav-btn { width: auto; white-space: nowrap; padding: 8px 14px; }
          .mnc-modal-header { padding: 14px 18px; }
          .mnc-modal-body { padding: 16px 18px; }
          .mnc-modal-footer { padding: 12px 18px; }
        }
      `}</style>
    </div>
  );
};

export default StudentPerformance;